import React, { useEffect, useState } from "react";
import {
  Box,
  Avatar,
  TableCell,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

import DescriptionIcon from "@mui/icons-material/Description";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BadgeIcon from "@mui/icons-material/Badge";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";

import CustomTable from "src/components/Table/Table";
import { useDispatch, useSelector } from "react-redux";

import {
  getCaptainsListAction,
  updateCaptainStatusAction,
  deleteCaptainAction,
  updateDocumentStatusAction,
} from "src/api/captains";

import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

export default function Captains() {
  const dispatch = useDispatch();

  const captainsList =
    useSelector((state: any) => state.captains?.captainsList) ||
    { result: { data: [], total: 0 } };

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    pageNo: 1,
    pageSize: 10,
  });

  const [openDocDialog, setOpenDocDialog] = useState(false);
  const [documents, setDocuments] = useState<any[]>([]);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  const [openCameraDialog, setOpenCameraDialog] = useState(false);
  const [cameraData, setCameraData] = useState<any>(null);

  const [openRouteDialog, setOpenRouteDialog] = useState(false);
  const [routeData, setRouteData] = useState<any[]>([]);

  const fetchCaptains = async (payload: any) => {
    try {
      setLoading(true);
      await dispatch(getCaptainsListAction(payload));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCaptains(formData);
  }, []);

  const fetchData = (data: any) => {
    const payload = {
      ...formData,
      pageNo: data.pageNumber,
      pageSize: data.pageSize,
    };
    setFormData(payload);
    fetchCaptains(payload);
  };

  const handleStatusChange = async (row: any, status: any) => {
    setLoading(true);
    await dispatch(updateCaptainStatusAction({ id: row.id, status }));
    fetchCaptains(formData);
    setLoading(false);
  };

  const handleDeleteClick = (row: any) => {
    setSelectedRow(row);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    await dispatch(deleteCaptainAction(selectedRow.id));
    fetchCaptains(formData);
    setDeleteOpen(false);
    setSelectedRow(null);
    setLoading(false);
  };

  const columns = [
    { field: "profile", headerName: "", width: 80 },
    { field: "name", headerName: "NAME", width: 180 },
    { field: "phone", headerName: "PHONE", width: 200 },
    { field: "createdAt", headerName: "CREATED AT", width: 220 },
    { field: "camera", headerName: "CAMERA", width: 150 },
    { field: "school", headerName: "SCHOOL", width: 220 },
    { field: "route", headerName: "ROUTE", width: 150 },
    { field: "status", headerName: "STATUS", width: 150 },
    { field: "action", headerName: "ACTION", width: 100 },
    { field: "docs", headerName: "", width: 160 },
  ];

  const vehicleColumns = [
    { field: "registrationNumber", headerName: "REG NUMBER" },
    { field: "model", headerName: "MODEL" },
    { field: "color", headerName: "COLOR" },
    { field: "seatCapacity", headerName: "SEATS" },
    { field: "insuranceExpiry", headerName: "INSURANCE" },
    { field: "pollutionExpiry", headerName: "PUC" },
  ];

  const actionContent = (column: any, row: any) => {
    if (column === "profile") {
      return (
        <TableCell align="center">
          <Avatar>{row.name?.charAt(0)}</Avatar>
        </TableCell>
      );
    }

    if (column === "camera") {
      return (
        <TableCell align="center">
          {row.camera ? (
            <Typography
              sx={{ textDecoration: "underline", cursor: "pointer", color: "#1976d2" }}
              onClick={() => {
                setCameraData(row.camera);
                setOpenCameraDialog(true);
              }}
            >
              View Details
            </Typography>
          ) : (
            "-"
          )}
        </TableCell>
      );
    }
    
    if (column === "school") {
      return (
        <TableCell align="center">
          {row.school ? (
            <Tooltip title={row.school.name}>
              <Typography
                noWrap
                sx={{
                  maxWidth: 180,
                  textDecoration: "underline",
                  cursor: "pointer",
                  color: "#1976d2",
                }}
                onClick={() => {
                  window.open(
                    `https://www.google.com/maps?q=${row.school.latitude},${row.school.longitude}`,
                    "_blank"
                  );
                }}
              >
                {row.school.name}
              </Typography>
            </Tooltip>
          ) : (
            "-"
          )}
        </TableCell>
      );
    }

    if (column === "route") {
      return (
        <TableCell align="center">
          {Array.isArray(row.route) && row.route.length > 0 ? (
            <Typography
              sx={{
                textDecoration: "underline",
                cursor: "pointer",
                color: "#1976d2",
                fontWeight: 500,
              }}
              onClick={() => {
                console.log("ROUTE CLICKED:", row.route); // ✅ debug
                setRouteData([...row.route]); // force new reference
                setOpenRouteDialog(true);
              }}
            >
              View Routes
            </Typography>
          ) : (
            "-"
          )}
        </TableCell>
      );
    }

    if (column === "status") {
      return (
        <TableCell align="center">
          <FormControl size="small">
            <Select
              value={row.status}
              onChange={(e) => handleStatusChange(row, e.target.value)}
              sx={{
                backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
                color: row.status ? "green" : "red",
                height: "32px",
              }}
            >
              <MenuItem value={1}>Active</MenuItem>
              <MenuItem value={0}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </TableCell>
      );
    }

    if (column === "action") {
      return (
        <TableCell align="center">
          <Tooltip title="Delete">
            <IconButton onClick={() => handleDeleteClick(row)}>
              <DeleteOutlineIcon />
            </IconButton>
          </Tooltip>
        </TableCell>
      );
    }

    if (column === "docs") {
      return (
        <TableCell align="center">
          <Typography
            sx={{
              textDecoration: "underline",
              cursor: row.documents?.length ? "pointer" : "not-allowed",
              color: row.documents?.length ? "#1976d2" : "gray",
              fontWeight: 500,
            }}
            onClick={() => {
              if (!row.documents || row.documents.length === 0) return;
              setDocuments(row.documents);
              setOpenDocDialog(true);
            }}
          >
            Show Documents
          </Typography>
        </TableCell>
      );
    }

    return <TableCell align="center">{row[column]}</TableCell>;
  };

  return (
    <>
      {loading && <CustomBackdrop open={loading} />}
      <Box sx={{ mt: "20px", p: 2 }}></Box>
      <Box
        sx={{
          "& .MuiTableContainer-root": {
            overflowX: "auto",

            "&::-webkit-scrollbar": { height: "6px" },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "#f1f5f9",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#cbd5e1",
              borderRadius: "10px",
            },
          },

          "& .MuiTable-root": {
            minWidth: "1700px",
          },
        }}
      >
        <CustomTable
          rows={captainsList?.result?.data || []}
          columns={columns}
          total={captainsList?.result?.total || 0}
          expandable
          expandColumns={vehicleColumns}
          expandDataKey="vehicles"
          action={actionContent}
          fetchData={fetchData}
          resetPagination={() => {}}
        />
      </Box>

      {/* DOCUMENTS */}
      <Dialog open={openDocDialog} onClose={() => setOpenDocDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Documents
          <IconButton onClick={() => setOpenDocDialog(false)} sx={{ float: "right" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {documents.length === 0 && <Typography>No documents available</Typography>}

          <Box display="flex" flexDirection="column" gap={2}>
            {documents.map((doc: any, index: number) => {
              const getIcon = () => {
                if (doc.type === "License") return <BadgeIcon color="primary" />;
                if (doc.type === "RC") return <DirectionsCarIcon color="primary" />;
                return <DescriptionIcon color="primary" />;
              };

              return (
                <Box key={index} sx={{ display: "flex", gap: 2, p: 2, border: "1px solid #eee", borderRadius: 2 }}>
                  {getIcon()}

                  <Box flex={1}>
                    <Typography fontWeight={600}>{doc.type}</Typography>

                    {doc.image ? (
                      <img
                        src={doc.image}
                        alt={doc.type}
                        style={{ width: "100%", maxHeight: 140, borderRadius: 8, objectFit: "cover" }}
                      />
                    ) : (
                      <Typography fontSize={12}>No Image</Typography>
                    )}

                    <Box mt={1}>
                      {doc.status === 0 && (
                        <Box display="flex" gap={1}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={async () => {
                              setLoading(true);

                              await dispatch(
                                updateDocumentStatusAction({
                                  documentId: doc.id,
                                  status: 1,
                                  rejectionReason: "",
                                  reviewedBy: "",
                                })
                              );

                              await fetchCaptains(formData);

                              setDocuments((prev: any) =>
                                prev.map((d: any) =>
                                  d.id === doc.id
                                    ? {
                                        ...d,
                                        status: 1,
                                      }
                                    : d
                                )
                              );

                              setLoading(false);
                            }}
                          >
                            Accept
                          </Button>

                          <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={async () => {
                              setLoading(true);

                              await dispatch(
                                updateDocumentStatusAction({
                                  documentId: doc.id,
                                  status: 2,
                                  rejectionReason: "Document Rejected",
                                  reviewedBy: "",
                                })
                              );

                              await fetchCaptains(formData);

                              setDocuments((prev: any) =>
                                prev.map((d: any) =>
                                  d.id === doc.id
                                    ? {
                                        ...d,
                                        status: 2,
                                      }
                                    : d
                                )
                              );

                              setLoading(false);
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}

                      {doc.status === 1 && <Typography color="green">Accepted</Typography>}
                      {doc.status === 2 && <Typography color="red">Rejected</Typography>}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </DialogContent>
      </Dialog>

      {/* ROUTES */}
      <Dialog
        open={openRouteDialog}
        onClose={() => setOpenRouteDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
          Routes
          <IconButton onClick={() => setOpenRouteDialog(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {routeData.length === 0 ? (
            <Typography>No route available</Typography>
          ) : (
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              overflow="auto"
              py={2}
            >
              {routeData.map((r: any, i: number) => (
                <React.Fragment key={i}>
                  {/* NODE */}
                  <Box textAlign="center" minWidth={100}>
                    <Box
                      sx={{
                        width: 18,
                        height: 18,
                        borderRadius: "50%",
                        border: "3px solid black",
                        mx: "auto",
                        backgroundColor: "#fff",
                      }}
                    />

                    <Typography
                      fontSize={12}
                      mt={1}
                      sx={{
                        textDecoration: "underline",
                        cursor: "pointer",
                        color: "#1976d2",
                      }}
                      onClick={() => {
                        window.open(
                          `https://www.google.com/maps?q=${r.latitude},${r.longitude}`,
                          "_blank"
                        );
                      }}
                    >
                      {r.name}
                    </Typography>
                  </Box>

                  {/* LINE */}
                  {i !== routeData.length - 1 && (
                    <Box
                      sx={{
                        height: 4,
                        width: 40,
                        backgroundColor: "orange",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete Confirmation</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{selectedRow?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

        {/* ✅ CAMERA DIALOG (FINAL FIX) */}
      <Dialog open={openCameraDialog} onClose={() => setOpenCameraDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Camera Details
          <IconButton onClick={() => setOpenCameraDialog(false)} sx={{ float: "right" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {!cameraData ? (
            <Typography>No camera data available</Typography>
          ) : (
            <Box display="flex" flexDirection="column" gap={1}>
              <Typography><strong>Camera Number:</strong> {cameraData.cameraNumber || "-"}</Typography>
              <Typography><strong>Camera Key:</strong> {cameraData.cameraKey || "-"}</Typography>
              <Typography><strong>SIM Number:</strong> {cameraData.simNumber || "-"}</Typography>
              <Typography><strong>Status:</strong> {cameraData.status ?? "-"}</Typography>
              <Typography><strong>Validity:</strong> {cameraData.validity || "-"}</Typography>
              <Typography><strong>Last Recharge:</strong> {cameraData.lastRecharge || "-"}</Typography>
              <Typography><strong>Created At:</strong> {cameraData.createdAt || "-"}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}